import React, { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import '../styles/loadingScreen.css';

// Константы конфигурации
const CONFIG = {
  SPINE_PATHS: {
    JSON: '/loading/spine/Datum.json',
    ATLAS: '/loading/spine/Datum.atlas',
    TEXTURE: '/loading/spine/Datum.png',
  },
  APP: {
    BACKGROUND_COLOR: 0x072042, // matches --app-bg-color so notch area blends in
    RESOLUTION: 1,
    AUTO_DENSITY: false,
  },
  SKELETON: {
    DEFAULT_WIDTH: 1071.36,
    DEFAULT_HEIGHT: 1893.13,
    INIT_DELAY: 100,
  },
  POSITIONING: {
    BASE_SCREEN_HEIGHT: 915,
    BASE_Y_OFFSET: 130,
  },
};

/**
 * Получает размеры скелета из bounds или fallback на данные из JSON
 */
const getSkeletonDimensions = (spine) => {
  try {
    spine.x = 0;
    spine.y = 0;
    spine.skeleton.updateWorldTransform();
    const bounds = spine.skeleton.getBounds();
    
    if (bounds && bounds.width > 0 && bounds.height > 0) {
      return { width: bounds.width, height: bounds.height, bounds };
    }
    throw new Error('Bounds not available');
  } catch (error) {
    const skeletonData = spine.skeleton.data;
    const skeletonInfo = skeletonData.skeleton || {};
    return {
      width: skeletonInfo.width || CONFIG.SKELETON.DEFAULT_WIDTH,
      height: skeletonInfo.height || CONFIG.SKELETON.DEFAULT_HEIGHT,
      bounds: null,
    };
  }
};

/**
 * Вычисляет масштаб для покрытия всего экрана (cover mode)
 */
const calculateScale = (screenWidth, screenHeight, skeletonWidth, skeletonHeight) => {
  const scaleX = screenWidth / skeletonWidth;
  const scaleY = screenHeight / skeletonHeight;
  return Math.max(scaleX, scaleY);
};

/**
 * Вычисляет позицию X для центрирования анимации
 */
const calculatePositionX = (screenWidth, scale, skeletonWidth, offsetX, bounds) => {
  if (offsetX !== 0) {
    const skeletonCenterX = -offsetX;
    return screenWidth / 2 - skeletonCenterX * scale;
  }
  
  if (bounds && bounds.width > 0 && bounds.height > 0) {
    const boundsCenterX = bounds.x + bounds.width / 2;
    return screenWidth / 2 - boundsCenterX * scale;
  }
  
  const scaledWidth = skeletonWidth * scale;
  return screenWidth / 2 - scaledWidth / 2;
};

/**
 * Вычисляет позицию Y для центрирования анимации
 */
const calculatePositionY = (screenHeight, scale, skeletonHeight, offsetY, bounds) => {
  if (offsetY !== 0) {
    const skeletonCenterY = -offsetY;
    return screenHeight / 2 - skeletonCenterY * scale;
  }
  
  if (bounds && bounds.width > 0 && bounds.height > 0) {
    const boundsCenterY = bounds.y + bounds.height / 2;
    return screenHeight / 2 - boundsCenterY * scale;
  }
  
  const scaledHeight = skeletonHeight * scale;
  const calculatedY = screenHeight / 2 - scaledHeight / 2;
  
  if (calculatedY <= 0) {
    // Пропорциональное смещение для предотвращения пустоты внизу
    const { BASE_SCREEN_HEIGHT, BASE_Y_OFFSET } = CONFIG.POSITIONING;
    return (BASE_Y_OFFSET / BASE_SCREEN_HEIGHT) * screenHeight;
  }
  
  return calculatedY;
};

/**
 * Настраивает размер и позицию анимации
 */
const setupSpinePosition = (spine, screenWidth, screenHeight) => {
  const safeTop = window?.Telegram?.WebApp?.safeAreaInset?.top ?? 0;

  const { width: skeletonWidth, height: skeletonHeight, bounds } = getSkeletonDimensions(spine);
  // Scale to fill the viewport area below the notch so content isn't hidden
  const scale = calculateScale(screenWidth, screenHeight - safeTop, skeletonWidth, skeletonHeight);

  spine.scale.set(scale);

  const skeletonData = spine.skeleton.data;
  const skeletonInfo = skeletonData.skeleton || {};
  const offsetX = skeletonInfo.x || 0;
  const offsetY = skeletonInfo.y || 0;

  spine.x = calculatePositionX(screenWidth, scale, skeletonWidth, offsetX, bounds);
  // Position within the safe area; canvas background fills the notch area above
  spine.y = calculatePositionY(screenHeight - safeTop, scale, skeletonHeight, offsetY, bounds) + safeTop;
};

/**
 * Запускает первую доступную анимацию
 */
const startAnimation = (spine) => {
  if (!spine.state || !spine.skeleton || !spine.skeleton.data) {
    return;
  }
  
  const animations = spine.skeleton.data.animations || [];
  if (animations.length > 0) {
    try {
      spine.state.setAnimation(0, animations[0].name, true);
    } catch (error) {
      console.warn('Failed to set animation:', error);
    }
  }
};

/**
 * Настраивает canvas стили
 */
const setupCanvasStyles = (canvas) => {
  canvas.style.position = 'absolute';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.width = '100vw';
  canvas.style.height = '100vh';
};

const LoadingScreen = () => {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const spineRef = useRef(null);
  const resizeHandlerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let app = null;
    let spine = null;
    let isMounted = true;

    const initSpine = async () => {
      try {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Инициализация PIXI приложения
        app = new PIXI.Application();
        await app.init({
          width: screenWidth,
          height: screenHeight,
          backgroundColor: CONFIG.APP.BACKGROUND_COLOR,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (!isMounted || !containerRef.current) {
          app.destroy();
          return;
        }

        containerRef.current.appendChild(app.canvas);
        appRef.current = app;
        setupCanvasStyles(app.canvas);

        // Загрузка Spine ресурсов
        await PIXI.Assets.load([
          CONFIG.SPINE_PATHS.JSON,
          CONFIG.SPINE_PATHS.ATLAS,
          CONFIG.SPINE_PATHS.TEXTURE,
        ]);

        try {
          // Создание Spine анимации
          spine = Spine.from({
            skeleton: CONFIG.SPINE_PATHS.JSON,
            atlas: CONFIG.SPINE_PATHS.ATLAS,
            autoUpdate: true,
          });
          spineRef.current = spine;

          // Ожидание инициализации скелета
          await new Promise(resolve => setTimeout(resolve, CONFIG.SKELETON.INIT_DELAY));

          if (spine.skeleton && spine.skeleton.data) {
            setupSpinePosition(spine, app.screen.width, app.screen.height);
          }

          startAnimation(spine);
          app.stage.addChild(spine);

          // Обработчик изменения размера окна
          resizeHandlerRef.current = () => {
            if (!app || !spine || !isMounted || !spine.skeleton || !spine.skeleton.data) {
              return;
            }
            
            const newWidth = window.innerWidth;
            const newHeight = window.innerHeight;
            
            app.renderer.resize(newWidth, newHeight);
            setupSpinePosition(spine, newWidth, newHeight);
          };
          
          window.addEventListener('resize', resizeHandlerRef.current);
        } catch (spineError) {
          console.error('Error creating Spine animation:', spineError);
        }
      } catch (error) {
        console.error('Error loading Spine resource:', error);
      }
    };

    initSpine();

    return () => {
      isMounted = false;
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
      }
      if (appRef.current) {
        try {
          appRef.current.destroy(true, {
            children: true,
            texture: true,
            baseTexture: true,
          });
        } catch (e) {
          console.error('Error destroying PIXI app:', e);
        }
      }
    };
  }, []);

  return (
    <div className="loading-screen-container">
      <div ref={containerRef} className="loading-screen-canvas" />
    </div>
  );
};

export default LoadingScreen;

